import React from 'react';
import { Sliders, Clock, Music, Repeat } from 'lucide-react';

/**
 * Control panel for adjusting breathing parameters
 */
export default function ControlPanel({ 
  phaseDurations, 
  setPhaseDurations, 
  settings, 
  setSettings, 
  presets, 
  onApplyPreset,
  disabled 
}) {
  const handleDurationChange = (phase, value) => {
    const numValue = parseInt(value) || 0;
    setPhaseDurations(prev => ({
      ...prev,
      [phase]: Math.max(0, Math.min(12, numValue))
    }));
  };

  const handleCycleChange = (value) => {
    if (value === 'infinity') {
      setSettings(prev => ({ ...prev, totalCycles: Infinity }));
    } else {
      setSettings(prev => ({ ...prev, totalCycles: parseInt(value) || 1 }));
    }
  };

  return (
    <div className="space-y-6">
      {/* Presets */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <div className="flex items-center gap-2 mb-4">
          <Sliders className="w-5 h-5 text-white" />
          <h3 className="text-lg font-bold text-white">Quick Presets</h3>
        </div>
        
        <select
          onChange={(e) => {
            const preset = presets?.find(p => p?.name === e?.target?.value);
            if (preset) onApplyPreset(preset);
          }}
          disabled={disabled}
          className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-3 text-white disabled:opacity-50"
        >
          <option value="">Select a preset...</option>
          {presets?.map(preset => (
            <option key={preset?.name} value={preset?.name}>
              {preset?.name}
            </option>
          ))}
        </select>
      </div>
      {/* Phase Durations */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-white" />
          <h3 className="text-lg font-bold text-white">Phase Durations (seconds)</h3>
        </div>

        <div className="space-y-4">
          {/* Inhale */}
          <div>
            <label className="text-white/80 text-sm block mb-2">Inhale</label>
            <input
              type="range"
              min="1"
              max="12"
              value={phaseDurations?.inhale}
              onChange={(e) => handleDurationChange('inhale', e?.target?.value)}
              disabled={disabled}
              className="w-full"
            />
            <div className="flex justify-between text-white/60 text-xs mt-1">
              <span>1s</span>
              <span className="font-bold text-white">{phaseDurations?.inhale}s</span>
              <span>12s</span>
            </div>
          </div>

          {/* Hold (after inhale) */}
          <div>
            <label className="text-white/80 text-sm block mb-2">Hold (after inhale)</label>
            <input
              type="range"
              min="0"
              max="12"
              value={phaseDurations?.hold_in}
              onChange={(e) => handleDurationChange('hold_in', e?.target?.value)}
              disabled={disabled}
              className="w-full"
            />
            <div className="flex justify-between text-white/60 text-xs mt-1">
              <span>0s</span>
              <span className="font-bold text-white">{phaseDurations?.hold_in}s</span>
              <span>12s</span>
            </div>
          </div>

          {/* Exhale */}
          <div>
            <label className="text-white/80 text-sm block mb-2">Exhale</label>
            <input
              type="range"
              min="1"
              max="12"
              value={phaseDurations?.exhale}
              onChange={(e) => handleDurationChange('exhale', e?.target?.value)}
              disabled={disabled}
              className="w-full"
            />
            <div className="flex justify-between text-white/60 text-xs mt-1">
              <span>1s</span>
              <span className="font-bold text-white">{phaseDurations?.exhale}s</span>
              <span>12s</span>
            </div>
          </div>

          {/* Hold (after exhale) */}
          <div>
            <label className="text-white/80 text-sm block mb-2">Hold (after exhale)</label>
            <input
              type="range"
              min="0"
              max="12"
              value={phaseDurations?.hold_out}
              onChange={(e) => handleDurationChange('hold_out', e?.target?.value)}
              disabled={disabled}
              className="w-full"
            />
            <div className="flex justify-between text-white/60 text-xs mt-1">
              <span>0s</span>
              <span className="font-bold text-white">{phaseDurations?.hold_out}s</span>
              <span>12s</span>
            </div>
          </div>
        </div>
      </div>
      {/* Cycles & Audio */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <div className="space-y-4">
          {/* Cycles */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Repeat className="w-5 h-5 text-white" />
              <label className="text-white/80 text-sm">Number of Cycles</label>
            </div>
            <select
              value={settings?.totalCycles === Infinity ? 'infinity' : settings?.totalCycles}
              onChange={(e) => handleCycleChange(e?.target?.value)}
              disabled={disabled}
              className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-2 text-white disabled:opacity-50"
            >
              <option value="infinity">∞ Continuous</option>
              {[1, 2, 3, 5, 10, 15, 20]?.map(num => (
                <option key={num} value={num}>{num} cycles</option>
              ))}
            </select>
          </div>

          {/* Audio Controls */}
          <div className="pt-4 border-t border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <Music className="w-5 h-5 text-white" />
              <span className="text-white/80 text-sm font-medium">Audio Options</span>
            </div>
            
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings?.enableBeeps}
                  onChange={(e) => setSettings(prev => ({ ...prev, enableBeeps: e?.target?.checked }))}
                  disabled={disabled}
                  className="w-5 h-5 rounded"
                />
                <span className="text-white/80 text-sm">🔔 Soft beep on phase change</span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings?.enableMetronome}
                  onChange={(e) => setSettings(prev => ({ ...prev, enableMetronome: e?.target?.checked }))}
                  disabled={disabled}
                  className="w-5 h-5 rounded"
                />
                <span className="text-white/80 text-sm">🎵 Metronome click</span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings?.enableVibration}
                  onChange={(e) => setSettings(prev => ({ ...prev, enableVibration: e?.target?.checked }))}
                  disabled={disabled}
                  className="w-5 h-5 rounded"
                />
                <span className="text-white/80 text-sm">📳 Vibrate on mobile</span>
              </label>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings?.lowMotion}
                  onChange={(e) => setSettings(prev => ({ ...prev, lowMotion: e?.target?.checked }))}
                  className="w-5 h-5 rounded"
                />
                <span className="text-white/80 text-sm">♿ Low Motion Mode</span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}